/* =============================================================================
   Vantage — product page behaviour
   Copyright (c) 2026. All rights reserved.
   ========================================================================== */

(function (V) {
  'use strict';

  /* ---------------------------------------------------------------------
     <vt-variants> — reads the option inputs, finds the matching variant in
     the JSON payload, then updates price, availability, URL, media and the
     hidden variant id used by the add-to-cart form.
     --------------------------------------------------------------------- */

  V.define(
    'vt-variants',
    class extends HTMLElement {
      connectedCallback() {
        this.productUrl = this.dataset.url;
        this.sectionId = this.dataset.section;
        this.variants = this.readVariants();
        if (!this.variants.length) return;

        this.addEventListener('change', () => this.onChange());
      }

      readVariants() {
        var script = this.querySelector('[data-variant-data]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      selectedOptions() {
        return Array.from(this.querySelectorAll('fieldset')).map(function (group) {
          var checked = group.querySelector('input:checked');
          if (checked) return checked.value;
          var select = group.querySelector('select');
          return select ? select.value : null;
        });
      }

      onChange() {
        var chosen = this.selectedOptions();

        this.querySelectorAll('fieldset').forEach(function (group) {
          var readout = group.querySelector('[data-option-value]');
          var checked = group.querySelector('input:checked');
          if (readout && checked) readout.textContent = checked.value;
        });

        var match = this.variants.find(function (variant) {
          return chosen.every(function (value, index) {
            return value === null || variant.options[index] === value;
          });
        });

        this.markUnavailable(chosen);

        if (!match) return this.setUnavailable();

        this.updateUrl(match);
        this.updateIdInput(match);
        this.updateMedia(match);
        this.updateInfo(match);

        V.emit('variant:change', { variant: match, section: this.sectionId });
      }

      /** Strike through option values that produce no purchasable variant. */
      markUnavailable(chosen) {
        var self = this;
        this.querySelectorAll('fieldset').forEach(function (group, index) {
          group.querySelectorAll('input').forEach(function (input) {
            var probe = chosen.slice();
            probe[index] = input.value;
            var exists = self.variants.some(function (variant) {
              return (
                variant.available &&
                probe.every(function (value, i) {
                  return value === null || variant.options[i] === value;
                })
              );
            });
            var label = group.querySelector('label[for="' + input.id + '"]');
            if (label) label.toggleAttribute('data-unavailable', !exists);
          });
        });
      }

      updateUrl(variant) {
        if (!this.productUrl || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }

      updateIdInput(variant) {
        document
          .querySelectorAll('input[name="id"][form="' + this.dataset.form + '"], vt-add-to-cart input[name="id"]')
          .forEach(function (input) {
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        if (!variant.featured_media) return;
        var gallery = document.querySelector('vt-gallery');
        if (gallery && gallery.showMedia) gallery.showMedia(String(variant.featured_media.id));
      }

      updateInfo(variant) {
        if (!this.sectionId) return this.setLocalState(variant);

        var url = this.productUrl + '?variant=' + variant.id + '&section_id=' + this.sectionId;

        fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');

            ['[data-price-block]', '[data-stock-block]', '[data-sku-block]'].forEach(function (selector) {
              var incoming = parsed.querySelector(selector);
              var current = document.querySelector(selector);
              if (incoming && current) current.innerHTML = incoming.innerHTML;
            });

            var incomingButton = parsed.querySelector('[data-buy-button]');
            var currentButton = document.querySelector('[data-buy-button]');
            if (incomingButton && currentButton) currentButton.innerHTML = incomingButton.innerHTML;
          })
          .catch(function () {});
      }

      setLocalState(variant) {
        var button = this.parentElement.querySelector('[data-buy-button] button');
        if (!button) return;
        button.disabled = !variant.available;
        button.querySelector('.btn__label').textContent = variant.available
          ? V.strings.addToCart
          : V.strings.soldOut;
      }

      setUnavailable() {
        var button = document.querySelector('[data-buy-button] button');
        if (!button) return;
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        var label = button.querySelector('.btn__label');
        if (label) label.textContent = V.strings.unavailable;
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-gallery> — a scroll-snap slider for the main media plus a thumbnail
     rail that pages with arrow buttons.

     The track is a plain overflow container, so swiping works before the
     script runs; hydration only adds the arrows, the thumbnail sync and the
     keyboard shortcuts. Scrollbars are hidden in CSS because the rails are
     dragged directly on touch.
     --------------------------------------------------------------------- */

  V.define(
    'vt-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        if (!this.track) return;

        // Cells are everything in the track; slides are the real media only.
        this.cells = Array.from(this.track.children);
        this.slides = this.cells.filter(function (cell) {
          return !cell.hasAttribute('data-clone');
        });
        if (this.slides.length === 0) return;

        this.loop = this.hasAttribute('data-loop') && this.cells.length > this.slides.length;

        this.nav = this.querySelector('[data-thumb-nav]');
        this.rail = this.querySelector('[data-thumb-rail]');
        this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
        this.counter = this.querySelector('[data-gallery-counter]');

        this.prevButton = this.querySelector('[data-slide-prev]');
        this.nextButton = this.querySelector('[data-slide-next]');
        this.railPrev = this.querySelector('[data-thumb-prev]');
        this.railNext = this.querySelector('[data-thumb-next]');

        this.index = Math.max(
          0,
          this.slides.findIndex(function (slide) {
            return slide.hasAttribute('data-active');
          })
        );

        // Revealing the clones shifts the track, so this has to happen before
        // anything measures a position — and the placement below has to happen
        // in the same frame, or the first paint shows the leading clone.
        this.setAttribute('data-ready', '');

        this.bind();
        this.setActive(this.index);
        this.scrollToIndex(this.index, 'auto');
        this.revealThumb(this.index, 'auto');
        this.measure();
      }

      /* ----- wiring ----- */

      bind() {
        this.thumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', () => this.select(index));
        });

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
        if (this.railPrev) this.railPrev.addEventListener('click', () => this.pageRail(-1));
        if (this.railNext) this.railNext.addEventListener('click', () => this.pageRail(1));

        this.track.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });
        if (this.rail) this.rail.addEventListener('scroll', () => this.updateRailArrows(), { passive: true });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          if (event.target.closest('input, select, textarea')) return;
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
              this.scrollToIndex(this.index, 'auto');
              this.measure();
            }, 120);
          });
          this.resizeObserver.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        clearTimeout(this.settleTimer);
        clearTimeout(this.resizeTimer);
        cancelAnimationFrame(this.scrollFrame);
      }

      /* ----- navigation ----- */

      select(index) {
        if (index < 0 || index >= this.slides.length) return;
        this.setActive(index);
        this.revealThumb(index);
        this.scrollToIndex(index);
      }

      /**
       * Moves one cell along. With the clones in place a step off either end
       * still travels in the direction asked for; normalise() then swaps the
       * clone for the slide it copies, which looks like nothing happened.
       */
      step(delta) {
        var target = this.nearestCell() + delta;

        if (!this.loop) {
          if (target < 0 || target >= this.cells.length) return;
        } else {
          target = Math.min(Math.max(target, 0), this.cells.length - 1);
        }

        var cell = this.cells[target];
        if (!cell) return;

        this.setActive(this.realIndexOf(cell));
        this.revealThumb(this.index);
        this.scrollToCell(target);
      }

      realIndexOf(cell) {
        var index = Number(cell.dataset.realIndex);
        return isNaN(index) ? 0 : index;
      }

      /** The track position of a real slide, accounting for a leading clone. */
      cellIndexOf(index) {
        return this.cells.indexOf(this.slides[index]);
      }

      nearestCell() {
        var position = this.track.scrollLeft;
        var nearest = 0;
        var shortest = Infinity;

        this.cells.forEach((cell, index) => {
          var distance = Math.abs(this.offsetWithin(this.track, cell, 'x') - position);
          if (distance < shortest) {
            shortest = distance;
            nearest = index;
          }
        });

        return nearest;
      }

      scrollToCell(cellIndex, behavior) {
        var cell = this.cells[cellIndex];
        if (!cell) return;
        this.track.scrollTo({
          left: this.offsetWithin(this.track, cell, 'x'),
          behavior: behavior || this.motion()
        });
      }

      /**
       * If the track came to rest on a clone, jump to the real slide it copies.
       * The two look identical, so the correction is never seen.
       */
      normalise() {
        if (!this.loop) return;

        var cell = this.cells[this.nearestCell()];
        if (!cell || !cell.hasAttribute('data-clone')) return;

        this.scrollToCell(this.cellIndexOf(this.realIndexOf(cell)), 'auto');
      }

      /** Public API — the variant picker calls this when featured media changes. */
      showMedia(mediaId) {
        if (!mediaId) return;
        var index = this.slides.findIndex(function (slide) {
          return slide.dataset.mediaId === String(mediaId);
        });
        if (index > -1) this.select(index);
      }

      /**
       * Distance from a scroll container's content origin to a child, measured
       * from rects so it holds whatever the offsetParent chain looks like.
       */
      offsetWithin(container, child, axis) {
        var containerRect = container.getBoundingClientRect();
        var childRect = child.getBoundingClientRect();
        if (axis === 'y') return childRect.top - containerRect.top + container.scrollTop;
        return childRect.left - containerRect.left + container.scrollLeft;
      }

      scrollToIndex(index, behavior) {
        this.scrollToCell(this.cellIndexOf(index), behavior);
      }

      motion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      /**
       * Keeps the reported index in step with a swipe. The highlight follows
       * the scroll frame by frame, but the rail is only nudged once scrolling
       * has settled — otherwise a long animated jump makes the rail chase
       * every slide it passes over on the way.
       */
      onTrackScroll() {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.normalise();
          this.revealThumb(this.index);
        }, 140);

        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          var cell = this.cells[this.nearestCell()];
          if (!cell) return;
          var nearest = this.realIndexOf(cell);
          if (nearest !== this.index) this.setActive(nearest);
        });
      }

      setActive(index) {
        this.index = index;
        var mediaId = this.slides[index] ? this.slides[index].dataset.mediaId : null;

        this.slides.forEach(function (slide, i) {
          slide.toggleAttribute('data-active', i === index);
          if (i === index) return;
          var video = slide.querySelector('video');
          if (video && !video.paused) video.pause();
        });

        this.thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-current', String(i === index));
        });

        if (this.counter) this.counter.textContent = index + 1 + ' / ' + this.slides.length;

        // A looping slider has no ends, so both arrows stay live throughout.
        if (this.prevButton) this.prevButton.disabled = !this.loop && index === 0;
        if (this.nextButton) this.nextButton.disabled = !this.loop && index === this.slides.length - 1;

        if (mediaId) V.emit('gallery:change', { mediaId: mediaId, index: index });
      }

      /* ----- thumbnail rail ----- */

      isColumn() {
        return this.rail ? getComputedStyle(this.rail).flexDirection === 'column' : false;
      }

      /** Scrolls the rail the minimum distance needed to expose a thumbnail. */
      revealThumb(index, behavior) {
        var thumb = this.thumbs[index];
        if (!thumb || !this.rail) return;

        behavior = behavior || this.motion();

        if (this.isColumn()) {
          var top = this.offsetWithin(this.rail, thumb, 'y');
          var bottom = top + thumb.offsetHeight;
          if (top < this.rail.scrollTop) {
            this.rail.scrollTo({ top: top, behavior: behavior });
          } else if (bottom > this.rail.scrollTop + this.rail.clientHeight) {
            this.rail.scrollTo({ top: bottom - this.rail.clientHeight, behavior: behavior });
          }
          return;
        }

        var left = this.offsetWithin(this.rail, thumb, 'x');
        var right = left + thumb.offsetWidth;
        if (left < this.rail.scrollLeft) {
          this.rail.scrollTo({ left: left, behavior: behavior });
        } else if (right > this.rail.scrollLeft + this.rail.clientWidth) {
          this.rail.scrollTo({ left: right - this.rail.clientWidth, behavior: behavior });
        }
      }

      /** Moves the rail by one full page of thumbnails. */
      pageRail(direction) {
        if (!this.rail) return;
        var behavior = this.motion();

        if (this.isColumn()) {
          this.rail.scrollBy({ top: direction * this.rail.clientHeight, behavior: behavior });
        } else {
          this.rail.scrollBy({ left: direction * this.rail.clientWidth, behavior: behavior });
        }
      }

      measure() {
        if (!this.rail || !this.nav) return;

        var column = this.isColumn();
        var overflows = column
          ? this.rail.scrollHeight - this.rail.clientHeight > 1
          : this.rail.scrollWidth - this.rail.clientWidth > 1;

        this.nav.toggleAttribute('data-overflow', overflows);
        this.updateRailArrows();
      }

      updateRailArrows() {
        if (!this.rail || !this.railPrev || !this.railNext) return;

        var column = this.isColumn();
        var position = column ? this.rail.scrollTop : this.rail.scrollLeft;
        var visible = column ? this.rail.clientHeight : this.rail.clientWidth;
        var total = column ? this.rail.scrollHeight : this.rail.scrollWidth;

        this.railPrev.disabled = position <= 1;
        this.railNext.disabled = position + visible >= total - 1;
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-recommendations> — loads related products once in view.
     --------------------------------------------------------------------- */

  V.define(
    'vt-recommendations',
    class extends HTMLElement {
      connectedCallback() {
        var url =
          V.routes.recommendations +
          '?section_id=' +
          this.dataset.section +
          '&product_id=' +
          this.dataset.productId +
          '&limit=' +
          (this.dataset.limit || 4);

        fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then((html) => {
            var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('vt-recommendations');
            if (!incoming || !incoming.innerHTML.trim()) return this.remove();
            this.innerHTML = incoming.innerHTML;
          })
          .catch(() => this.remove());
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-collection-filters> — submits facets over Ajax and swaps the grid.
     --------------------------------------------------------------------- */

  V.define(
    'vt-collection-filters',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.form.addEventListener('change', () => this.apply());

        window.addEventListener('popstate', () => {
          window.location.reload();
        });
      }

      apply() {
        var params = new URLSearchParams(new FormData(this.form)).toString();
        var url = window.location.pathname + (params ? '?' + params : '');

        var grid = document.querySelector('[data-product-grid]');
        if (grid) grid.style.opacity = '0.5';

        fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');

            ['[data-product-grid]', '[data-facet-count]', '.pagination'].forEach(function (selector) {
              var incoming = parsed.querySelector(selector);
              var current = document.querySelector(selector);
              if (incoming && current) current.innerHTML = incoming.innerHTML;
              else if (!incoming && current) current.innerHTML = '';
            });

            window.history.pushState({}, '', url);
          })
          .finally(function () {
            if (grid) grid.style.opacity = '';
          });
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-country-selector> — reveals the province field only when needed.
     --------------------------------------------------------------------- */

  V.define(
    'vt-localization',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', function (event) {
          var option = event.target.closest('[data-value]');
          if (!option) return;
          event.preventDefault();
          var input = this.querySelector('input[name="' + this.dataset.field + '"]');
          if (!input) return;
          input.value = option.dataset.value;
          this.querySelector('form').submit();
        });
      }
    }
  );
})(window.Vantage);


/* ---------------------------------------------------------------------
     Alert Massege Disable Cart button if product is Sold out
     --------------------------------------------------------------------- */

// // Sold Out alert with Sad Icon & "Sorry" Title
// window.showSoldOutAlert = function(msg, e) {
//   if (e) e.preventDefault();
  
//   let overlay = document.getElementById('custom-soldout-modal-overlay');
  
//   if (!overlay) {
//     overlay = document.createElement('div');
//     overlay.id = 'custom-soldout-modal-overlay';
//     overlay.className = 'custom-modal-overlay';
    
//     overlay.innerHTML = `
//       <div class="custom-alert-modal">
//         <button type="button" class="custom-modal-close" onclick="closeSoldOutAlert()">&times;</button>
//         <div class="custom-modal-icon">
//           <!-- Sad Face SVG Icon -->
//           <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="44" height="44">
//             <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
//             <!-- Eyes -->
//             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 10h.01M15 10h.01" />
//             <!-- Sad Downturned Mouth -->
//             <path stroke-linecap="round" stroke-linejoin="round" d="M16 16c-1-1.3-2.3-1.8-4-1.8s-3 .5-4 1.8" />
//           </svg>
//         </div>
//         <h3 class="custom-modal-title">Sorry</h3>
//         <p class="custom-modal-text" id="custom-soldout-modal-msg"></p>
//         <button type="button" class="custom-modal-action" onclick="closeSoldOutAlert()">Close</button>
//       </div>
//     `;
//     document.body.appendChild(overlay);

//     window.closeSoldOutAlert = function() {
//       const el = document.getElementById('custom-soldout-modal-overlay');
//       if (el) el.classList.remove('is-visible');
//     };

//     overlay.addEventListener('click', function(event) {
//       if(event.target === overlay) window.closeSoldOutAlert();
//     });
//   }

//   document.getElementById('custom-soldout-modal-msg').textContent = msg;
  
//   requestAnimationFrame(() => {
//     overlay.classList.add('is-visible');
//   });
// };



window.showSoldOutAlert = function(msg, e, productName, productUrl) {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  }
  
  let pUrl = productUrl || window.location.href;
  let pName = productName || document.title;

  let overlay = document.getElementById('custom-soldout-modal-overlay');
  
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'custom-soldout-modal-overlay';
    overlay.className = 'custom-modal-overlay';
    
    overlay.innerHTML = `
      <div class="custom-alert-modal">
        <button type="button" class="custom-modal-close" onclick="closeSoldOutAlert()">&times;</button>
        
        <!-- STEP 1: Sad Icon & Sorry View -->
        <div id="modal-step-1">
          <div class="custom-modal-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="44" height="44">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 10h.01M15 10h.01" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M16 16c-1-1.3-2.3-1.8-4-1.8s-3 .5-4 1.8" />
            </svg>
          </div>
          <h3 class="custom-modal-title">Sorry</h3>
          <p class="custom-modal-text" id="custom-soldout-modal-msg"></p>
          
          <div style="margin-top: 15px;">
            <button type="button" class="custom-modal-action" onclick="switchToNotifyForm()" style="width: 100%; border: none; cursor: pointer;">
              Notify Me When Available
            </button>
          </div>
        </div>

        <!-- STEP 2: Email & Phone Form View -->
        <div id="modal-step-2" style="display: none;">
          <h3 class="custom-modal-title" style="margin-bottom: 5px;">Get Notified</h3>
          <p style="font-size: 13px; color: #666; margin-bottom: 15px;">Enter your details and we will email you as soon as this product is back.</p>
          
          <form action="/contact" method="post" id="soldout-notify-form" onsubmit="handleNotifySubmit(event)">
            <input type="hidden" name="form_type" value="contact">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="contact[body]" id="form-body-content">
            
            <div style="margin-bottom: 12px; text-align: left;">
              <label style="font-size: 12px; font-weight: 600; display: block; margin-bottom: 4px;">Email Address *</label>
              <input type="email" name="contact[email]" required placeholder="name@example.com" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px;">
            </div>

            <div style="margin-bottom: 15px; text-align: left;">
              <label style="font-size: 12px; font-weight: 600; display: block; margin-bottom: 4px;">Phone Number (Optional)</label>
              <input type="tel" name="contact[phone]" placeholder="+92 300 1234567" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px;">
            </div>

            <button type="submit" class="custom-modal-action" style="width: 100%; border: none; cursor: pointer;">
              Send Request
            </button>
          </form>
          
          <div id="form-success-msg" style="display: none; padding: 20px 0; color: #2e7d32; font-weight: 600;">
            ✓ Thank you! We have received your request.
          </div>
        </div>

      </div>
    `;
    document.body.appendChild(overlay);

    window.closeSoldOutAlert = function() {
      const el = document.getElementById('custom-soldout-modal-overlay');
      if (el) {
        el.classList.remove('is-visible');
        setTimeout(() => {
          document.getElementById('modal-step-1').style.display = 'block';
          document.getElementById('modal-step-2').style.display = 'none';
          document.getElementById('soldout-notify-form').style.display = 'block';
          document.getElementById('form-success-msg').style.display = 'none';
          document.getElementById('soldout-notify-form').reset();
        }, 300);
      }
    };

    window.switchToNotifyForm = function() {
      document.getElementById('modal-step-1').style.display = 'none';
      document.getElementById('modal-step-2').style.display = 'block';
      
      let bodyText = `[BACK IN STOCK REQUEST]\n\nProduct Name: ${window.currentProductName || 'N/A'}\nProduct Link: ${window.currentProductUrl || window.location.href}`;
      document.getElementById('form-body-content').value = bodyText;
    };

    window.handleNotifySubmit = function(event) {
      event.preventDefault();
      let form = document.getElementById('soldout-notify-form');
      let formData = new FormData(form);

      fetch('/contact', {
        method: 'POST',
        body: formData
      }).then(() => {
        form.style.display = 'none';
        document.getElementById('form-success-msg').style.display = 'block';
        setTimeout(() => {
          window.closeSoldOutAlert();
        }, 2500);
      }).catch(() => {
        alert('Something went wrong. Please try again.');
      });
    };

    overlay.addEventListener('click', function(event) {
      if(event.target === overlay) window.closeSoldOutAlert();
    });
  }

  window.currentProductName = pName;
  window.currentProductUrl = pUrl;

  document.getElementById('custom-soldout-modal-msg').textContent = msg;
  
  requestAnimationFrame(() => {
    overlay.classList.add('is-visible');
  });
};

// Global click listener to catch both product card and product page buttons securely
document.addEventListener('click', function(e) {
  const trigger = e.target.closest('.sold-out-alert-trigger');
  if (trigger) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
    
    const title = trigger.getAttribute('data-product-title') || document.title;
    const url = trigger.getAttribute('data-product-url') || window.location.href;
    
    window.showSoldOutAlert('Sorry, we’re currently out of stock. Please check back soon.', e, title, url);
  }
}, true);